import React, { ReactNode } from 'react';
import _orderBy from 'lodash/orderBy';

import { IconPlayFilm, Tooltip } from '@trqk/components';
import { locale, formatDollarsStatement, removeNulls } from '@trqk/utils';

import { DataCard, DataCardTable } from '../components';
import { TopFiveProgramsRow } from '.';
import { URL_PARAMS } from '../../../../../../constants';

const earningsColor = 'quarternary';

const getSearchParams = (
    show: string,
    source?: string,
): URLSearchParam[] | null => {
    const paramsWithShowOnly = [
        {
            key: URL_PARAMS.PROGRAM,
            value: show,
        },
    ];

    if (show.length > 0 && source && source.length > 0) {
        return [
            ...paramsWithShowOnly,
            {
                key: URL_PARAMS.CHANNEL,
                value: source,
            },
        ];
    }

    if (show.length > 0) {
        return paramsWithShowOnly;
    }

    return null;
};

interface TopFiveProgramsCardProps {
    data: Maybe<InsightTopFiveProgramsUserData>[];
    entityName: ReactNode;
}

export const TopFiveProgramsCard = ({
    data,
    entityName,
}: TopFiveProgramsCardProps): JSX.Element => {
    const sortedAndSlicedData = _orderBy(
        removeNulls<InsightTopFiveProgramsUserData>(data),
        ['earnings'],
        ['desc'],
    ).slice(0, 5);
    const cleanedData = sortedAndSlicedData.map(
        (
            item: InsightTopFiveProgramsUserData,
        ): InsightTopFiveProgramsUserData => {
            if (
                typeof item?.source === 'string' &&
                ['not_reported', 'not reported'].includes(item.source)
            ) {
                return { ...item, source: '' };
            }

            return item;
        },
    );

    return (
        <DataCard
            title={
                locale.enUS.INSIGHTS.CARDS.TOP_FIVE.TOP_EARNING_PROGRAMS.TITLE
            }
            color={earningsColor}
            header={
                <Tooltip
                    message={entityName}
                    position="bottom"
                    background="light"
                    minWidth={300}
                >
                    <div className="mx-10 text-center leading-6 clamp-2-lines-break-words">
                        {
                            locale.enUS.INSIGHTS.CARDS.TOP_FIVE
                                .TOP_EARNING_PROGRAMS.HEADER.PART_1
                        }{' '}
                        {entityName}
                    </div>
                </Tooltip>
            }
            Icon={IconPlayFilm}
            inInsightsIndex={false}
        >
            <DataCardTable>
                {cleanedData.map(
                    (
                        {
                            show,
                            earnings,
                            source,
                        }: InsightTopFiveProgramsUserData,
                        index: number,
                    ): JSX.Element => {
                        const earningsFormatted =
                            formatDollarsStatement(earnings);

                        const sourceParam =
                            !source || source.toLowerCase() === 'none'
                                ? undefined
                                : source;

                        const searchParams = getSearchParams(show, sourceParam);

                        return (
                            <TopFiveProgramsRow
                                key={`${show}.${source}`}
                                earningsColor={earningsColor}
                                earningsFormatted={earningsFormatted}
                                index={index}
                                searchParams={searchParams}
                                show={show}
                                source={source ?? null}
                            />
                        );
                    },
                )}
            </DataCardTable>
        </DataCard>
    );
};
